import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Accordion, OverlayTrigger, Popover } from "react-bootstrap";
import { isFuture } from "date-fns";
import moment from "moment";

import DocumentsTable from "./DocumentsTable";

import {
  COLOR_DANGER,
  COLOR_GRAY2,
  COLOR_GREEN,
  COLOR_WARNING,
} from "../../Colors";
import { formatDate } from "../../helpers/formatDate";
import { isIrregularDocument } from "../../helpers/isIrregularDocument";
import EmployeeProgress from "./EmployeeProgress";

function EmployeeListItem({
  index,
  employee,
  employeeRef,
  absenceInitialDate,
  absenceFinalDate,
  documents = [],
  showingDocumentsFor = [],
  expandedDocumentsFor = [],
  percentage,
  color,
  scroll,
  appData,
  onIrregularDocumentsDismiss,
  onDocumentOpen,
  onUploadOpen,
  onDocumentDismiss,
  onEdit,
  onDocumentsAccordionToggle,
  onDocumentsExpand,
  onShowContracts,
}) {
  const [isAccordionOpen, setIsAccordionOpen] = useState(
    showingDocumentsFor?.includes(employee.funcionario)
  );

  const hasIrregularDocuments = useMemo(() => !!employee.invalidos, [employee]);

  const irregularDocuments = useMemo(
    () => documents.filter(isIrregularDocument),
    [documents]
  );

  const controlaDocumentosFuncionario = useMemo(
    () => appData?.controlaDocumentosFuncionario,
    [appData],
  )

  const handleAccordionOnSelect = useCallback(
    (key) => {
      if (onDocumentsAccordionToggle) onDocumentsAccordionToggle(key, employee);
      setIsAccordionOpen(key !== null);
    },
    [onDocumentsAccordionToggle, employee]
  );

  const handleOpen = useCallback(
    (document, employee, isExpanded) =>
      onDocumentOpen(employee, document, isExpanded),
    [onDocumentOpen]
  );

  const handleUpload = useCallback(
    (document, employee, isExpanded) =>
      onUploadOpen(employee, document, isExpanded),
    [onUploadOpen]
  );

  return (
    <Accordion
      key={employee.funcionario}
      activeKey={isAccordionOpen ? "1" : "0"}
      className="d-flex flex-column"
      onSelect={handleAccordionOnSelect}
      style={{
        borderBottom: "2px solid #c8c8c8",
      }}
    >
      <div
        className={`row m-0 justify-content-center pb-3 ${
          employee.invalidos === 0 ? "success" : "warning"
        } ${!index ? "mt-0" : "mt-3"}`}
      >
        <div
          className="col-auto p-0 d-flex justify-content-center"
          style={{ minWidth: 80 }}
        >
          {employee.invalidos > 0 && (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{
                color: employee.invalidos > 0 ? COLOR_DANGER : COLOR_WARNING,
              }}
            >
              <FontAwesomeIcon icon="exclamation-triangle" />
              Irregular
              <br />
            </div>
          )}
          {employee.invalidos === 0 && (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{ color: COLOR_GREEN }}
            >
              <FontAwesomeIcon icon="check" />
              Regular
              <br />
            </div>
          )}
        </div>
        <div className="d-flex align-items-center row col">
          <div className="col-12 col-xl-7">
            <div className="col-auto d-flex align-items-center">
              <h3
                ref={
                  employee.funcionario === scroll?.idFuncionario
                    ? employeeRef
                    : null
                }
                className="font-weight-600 mr-2 mb-0 text-uppercase"
                style={{ fontSize: "14px" }}
              >
                {employee.nome}
              </h3>
              {employee.afastamento && isFuture(absenceFinalDate) && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Popover className="p-1" id="afastamento-tooltip">
                      {formatDate(absenceInitialDate)} até{" "}
                      {formatDate(absenceFinalDate)}
                    </Popover>
                  }
                >
                  <small
                    className="badge-pill badge-danger py-1 px-2 mx-1 font-weight-bold text-uppercase"
                    style={{ background: "#f15923" }}
                  >
                    {employee.afastamento?.tipoAfastamentoNome}
                  </small>
                </OverlayTrigger>
              )}
            </div>
            <div className="col-auto d-flex flex-column mt-2">
              <div className="col-12 d-flex p-0">
                <div className="col d-flex flex-column text-center text-md-left p-0">
                  <div className="row text-left">
                    <div className="col-5">
                      <span
                        style={{
                          color: COLOR_GRAY2,
                          fontSize: "14px",
                        }}
                      >
                        Cargo:
                      </span>
                    </div>
                    <div className="col-7">
                      <span
                        className="text-uppercase"
                        style={{
                          color: COLOR_GRAY2,
                          fontSize: "14px",
                        }}
                      >
                        <b>{employee.cargo}</b>
                      </span>
                    </div>
                  </div>
                  <div className="row text-left">
                    <div className="col-5">
                      <span
                        style={{
                          color: COLOR_GRAY2,
                          fontSize: "14px",
                        }}
                      >
                        CPF:
                      </span>
                    </div>
                    <div className="col-7">
                      <span
                        style={{
                          color: COLOR_GRAY2,
                          fontSize: "14px",
                        }}
                      >
                        <b>{employee.cpf}</b>
                      </span>
                    </div>
                  </div>
                  <div className="row text-left">
                    <div className="col-5">
                      <span
                        style={{
                          color: COLOR_GRAY2,
                          fontSize: "14px",
                        }}
                      >
                        Data Admissão:
                      </span>
                    </div>
                    <div className="col-7">
                      <span
                        style={{
                          color: COLOR_GRAY2,
                          fontSize: "14px",
                        }}
                      >
                        <b>
                          {moment(employee.dataAdmissao.date).format(
                            "DD/MM/YYYY"
                          )}
                        </b>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex">
                <button
                  type="button"
                  onClick={() => onEdit(employee.idFilialFuncionario)}
                  style={{
                    textDecoration: "underline",
                    fontSize: "12px",
                  }}
                  className="mt-2 mr-3 font-weight-600"
                >
                  EDITAR FUNCIONÁRIO
                </button>
                {controlaDocumentosFuncionario && (
                  <Accordion.Toggle
                    type="button"
                    eventKey="1"
                    style={{
                      textDecoration: "underline",
                      fontSize: "12px",
                    }}
                    className="mt-2 mr-3 font-weight-600"
                  >
                    {isAccordionOpen ? "OCULTAR" : `ABRIR`} DOCUMENTOS
                  </Accordion.Toggle>
                )}
                {controlaDocumentosFuncionario && hasIrregularDocuments && (
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Popover className="p-1">
                        Marcar todos os Documentos Irregulares do Funcionário
                        (expirado, expirando e pendentes) como Dispensados
                      </Popover>
                    }
                  >
                    <button
                      type="button"
                      onClick={() =>
                        onIrregularDocumentsDismiss(
                          irregularDocuments,
                          employee,
                          !irregularDocuments.length && hasIrregularDocuments
                        )
                      }
                      style={{
                        textDecoration: "underline",
                        fontSize: "12px",
                      }}
                      className="mt-2 font-weight-600"
                    >
                      DISPENSAR DOCUMENTOS PENDENTES
                    </button>
                  </OverlayTrigger>
                )}
              </div>
            </div>
          </div>
          {controlaDocumentosFuncionario && (
            <EmployeeProgress
              color={color}
              employee={employee}
              percentage={percentage}
            />
          )}
        </div>
      </div>
      <DocumentsTable
        employee={employee}
        documents={isAccordionOpen ? documents : []}
        handleEyeClick={handleOpen}
        handleFinalDateDbClick={handleOpen}
        handleInitialDateDbClick={handleOpen}
        handleNameDbClick={handleOpen}
        handleStatusDbClick={handleOpen}
        onUploadOpen={handleUpload}
        handleEmployeeDocumentDismissal={onDocumentDismiss}
        scroll={scroll}
        showingAllDocumentsDefault={expandedDocumentsFor?.includes(
          employee.funcionario
        )}
        onDocumentsExpand={onDocumentsExpand}
        onShowContracts={onShowContracts}
      />
    </Accordion>
  );
}

EmployeeListItem.propTypes = {
  index: PropTypes.number,
  employee: PropTypes.object,
  employeeRef: PropTypes.object,
  absenceInitialDate: PropTypes.any,
  absenceFinalDate: PropTypes.any,
  documents: PropTypes.array,
  showingDocumentsFor: PropTypes.array,
  expandedDocumentsFor: PropTypes.array,
  percentage: PropTypes.number,
  color: PropTypes.string,
  scroll: PropTypes.object,
  appData: PropTypes.object,
  onIrregularDocumentsDismiss: PropTypes.func,
  onDocumentOpen: PropTypes.func,
  onUploadOpen: PropTypes.func,
  onDocumentDismiss: PropTypes.func,
  onEdit: PropTypes.func,
  onDocumentsAccordionToggle: PropTypes.func,
  onDocumentsExpand: PropTypes.func,
  onShowContracts: PropTypes.func,
};

const mapStateToProps = (state) => ({
  appData: state.dados.data,
});

export default connect(mapStateToProps)(EmployeeListItem);
